/**
 * @file SystemLogViewer.js
 * @version 1.2.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-08-19
 * @modified 2026-10-02
 *
 * The searchable half of the admin logger — the log itself, filterable by
 * date, date range, source and kind.
 *
 * Where the widget previously read public.sync_history alone, this reads the
 * unified feed at /api/system-logs/entries, which spans every table the
 * system logs to: scheduled syncs, the audit trail (audit.change_log),
 * outbound email deliverability, the statement email queue, backfill job
 * state and the till sweep watermark.
 *
 * Rows stay grouped by date, as before, because that is how the log is read.
 * Filters persist across navigation via usePersistedState.
 *
 * An expanded line whose details carry a `note` / `fix_prompt` shows them as
 * text above the JSON (LogEntryNote), not as escaped strings inside it.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { API_URL } from '../config';
import usePersistedState from '../hooks/usePersistedState';
import useLatestRequest from '../hooks/useLatestRequest';
import { clearFiltersClass } from '../utils/activeFilters';
import Input from './Input';
import SearchHelp from './SearchHelp';
import DateInput from './DateInput';
import Dropdown from './Dropdown';
import MultiSelectDropdown from './MultiSelectDropdown';
import PaginationControls from './PaginationControls';
import LoadingSpinner from './LoadingSpinner';
import LogEntryNote, { noteOf, withoutNote } from './LogEntryNote';
import './SystemLogViewer.css';

const PAGE_SIZE = 150;

const SEVERITY_OPTIONS = [
  { value: 'all', label: 'All severities' },
  { value: 'concerning', label: 'Concerning (warn + error)' },
  { value: 'err', label: 'Errors only' },
  { value: 'warn', label: 'Warnings only' },
  { value: 'ok', label: 'Routine only' }
];

// Per-source accent, so a line's origin is readable at a glance without
// having to parse the text.
const SOURCE_ACCENT = {
  sync_history: '#79c0ff',
  change_log: '#d2a8ff',
  email_messages: '#ffa657',
  email_queue: '#e2e2a4',
  backfill: '#ffb87a',
  epos_sweep: '#56d364',
  ai_usage: '#f778ba'
};

const EMPTY_FILTERS = {
  from: '',
  to: '',
  sources: [],
  kinds: [],
  severity: 'all',
  search: ''
};

function pad(n) { return String(n).padStart(2, '0'); }

function localDateKey(iso) {
  if (!iso) return '????-??-??';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '????-??-??';
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function formatTime(iso) {
  if (!iso) return '??:??:??';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '??:??:??';
  return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

/**
 * sync_history rows carry their meaning in the details jsonb rather than a
 * text column, so the server leaves `summary` null for them and they are
 * summarised here — the same shape the widget has always shown.
 */
function summarise(entry) {
  if (entry.summary) return entry.summary;
  const d = entry.details;
  if (!d || typeof d !== 'object') return `records=${'?'}`;
  if (d.error) return `ERROR: ${String(d.error).slice(0, 200)}`;
  const parts = [];
  const pairs = [
    ['added', 'added'], ['updated', 'updated'], ['deleted', 'deleted'],
    ['errored', 'errored'], ['orphaned', 'orphaned'],
    ['invoicesVoided', 'voided'], ['invoicesBlocked', 'blocked'],
    ['date_errors', 'dateErrors'], ['imported', 'imported'],
    ['processed', 'processed'], ['skipped', 'skipped'],
    ['unchanged', 'unchanged'], ['total', 'total'], ['duration_ms', 'took']
  ];
  for (const [key, label] of pairs) {
    if (d[key] != null && typeof d[key] !== 'object') {
      parts.push(`${label}=${d[key]}${key === 'duration_ms' ? 'ms' : ''}`);
    }
  }
  return parts.length ? parts.join(' ') : Object.keys(d).slice(0, 4).join(', ');
}

function SystemLogViewer() {
  const [filters, setFilters] = usePersistedState('systemLogViewer.filters', EMPTY_FILTERS);
  const [page, setPage] = usePersistedState('systemLogViewer.page', 1);
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [sources, setSources] = useState([]);
  const [kinds, setKinds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedUid, setExpandedUid] = useState(null);
  const [expandedDates, setExpandedDates] = useState(() => new Set());

  // Debounced free-text search so typing doesn't fire a query per keystroke.
  // The updater returns the same object when nothing changed, so the effect
  // firing on mount (draft seeded from persisted filters) is a no-op and does
  // not knock the user off their restored page.
  const [searchDraft, setSearchDraft] = useState(filters.search || '');
  useEffect(() => {
    if (filters.search === searchDraft) return undefined;
    const handle = setTimeout(() => {
      setFilters(f => ({ ...f, search: searchDraft }));
      setPage(1);
    }, 350);
    return () => clearTimeout(handle);
  }, [searchDraft, filters.search, setFilters, setPage]);

  useEffect(() => {
    let cancelled = false;
    fetch(`${API_URL}/api/system-logs/sources`, { credentials: 'include' })
      .then(r => (r.ok ? r.json() : null))
      .then(data => {
        if (cancelled || !data) return;
        setSources(data.sources || []);
        setKinds(data.kinds || []);
      })
      .catch(() => { /* filters degrade to text search only */ });
    return () => { cancelled = true; };
  }, []);

  // Responses come back out of order; only the newest may write the rows.
  const latest = useLatestRequest();

  const fetchEntries = useCallback(async () => {
    const seq = latest.begin();
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.from) params.set('from', filters.from);
      if (filters.to) params.set('to', filters.to);
      if (filters.sources?.length) params.set('sources', filters.sources.join(','));
      if (filters.kinds?.length) params.set('kinds', filters.kinds.join(','));
      if (filters.severity && filters.severity !== 'all') params.set('severity', filters.severity);
      if (filters.search) params.set('search', filters.search);
      params.set('limit', String(PAGE_SIZE));
      params.set('offset', String((page - 1) * PAGE_SIZE));

      const res = await fetch(`${API_URL}/api/system-logs/entries?${params.toString()}`, {
        credentials: 'include'
      });
      if (!latest.isLatest(seq)) return;   // superseded by a later search
      if (res.status === 403) { setError('Forbidden — admin only.'); return; }
      if (!res.ok) { setError(`HTTP ${res.status}`); return; }
      const data = await res.json();
      if (!latest.isLatest(seq)) return;
      setEntries(data.entries || []);
      setTotal(data.total || 0);
      setError(null);
    } catch (e) {
      if (!latest.isLatest(seq)) return;
      setError(e.message || String(e));
    } finally {
      if (latest.isLatest(seq)) setLoading(false);
    }
  }, [filters, page, latest]);

  useEffect(() => { fetchEntries(); }, [fetchEntries]);

  const setFilter = (key, value) => {
    setFilters(f => ({ ...f, [key]: value }));
    setPage(1);
  };

  // Kind options track the chosen sources, so the list stays usable rather
  // than offering all 89 kinds at once.
  const kindOptions = useMemo(() => {
    const active = filters.sources?.length ? new Set(filters.sources) : null;
    return kinds
      .filter(k => !active || active.has(k.source))
      .map(k => ({ value: k.kind, label: k.kind, count: k.count, searchText: `${k.kind} ${k.source}` }));
  }, [kinds, filters.sources]);

  const grouped = useMemo(() => {
    const out = [];
    const idx = new Map();
    for (const e of entries) {
      const date = localDateKey(e.ts);
      let g = idx.get(date);
      if (!g) { g = { date, rows: [] }; idx.set(date, g); out.push(g); }
      g.rows.push(e);
    }
    return out;
  }, [entries]);

  // Newest date opens by default; deeper dates stay collapsed. Recomputed when
  // the result set changes so a filtered search lands on something visible.
  const newestDate = grouped[0]?.date;
  useEffect(() => {
    if (newestDate) setExpandedDates(new Set([newestDate]));
  }, [newestDate]);

  const toggleDate = (date) => {
    setExpandedDates(prev => {
      const next = new Set(prev);
      if (next.has(date)) next.delete(date); else next.add(date);
      return next;
    });
  };

  const activeFilterCount = [
    filters.from, filters.to, filters.search,
    filters.severity !== 'all' ? filters.severity : '',
    filters.sources?.length ? 'x' : '',
    filters.kinds?.length ? 'x' : ''
  ].filter(Boolean).length;

  const clearFilters = () => {
    setFilters(EMPTY_FILTERS);
    setSearchDraft('');
    setPage(1);
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="system-log-viewer">
      <div className="slv-filters">
        <div className="slv-filter slv-filter-search">
          <Input
            type="text"
            placeholder="Search log…"
            value={searchDraft}
            onChange={(e) => setSearchDraft(e.target.value)}
            onEnter={() => {
              // Now, not after the typing pause — and again if nothing changed.
              if (filters.search !== searchDraft) {
                setFilters(f => ({ ...f, search: searchDraft }));
                setPage(1);
              } else {
                fetchEntries();
              }
            }}
            icon="search"
          />
          <SearchHelp fields={['type', 'summary', 'who', 'record', 'details']} />
        </div>
        <div className="slv-filter slv-filter-date">
          <DateInput
            value={filters.from}
            onChange={(e) => setFilter('from', e.target.value)}
            placeholderText="From"
            isClearable
          />
        </div>
        <div className="slv-filter slv-filter-date">
          <DateInput
            value={filters.to}
            onChange={(e) => setFilter('to', e.target.value)}
            placeholderText="To"
            isClearable
          />
        </div>
        <div className="slv-filter slv-filter-source">
          <MultiSelectDropdown
            value={filters.sources || []}
            onChange={(v) => { setFilter('sources', v); setFilter('kinds', []); }}
            options={sources.map(s => ({ value: s.key, label: s.label }))}
            placeholder="All sources"
            hideTags
            summarize={(v) => (v.length === 0 ? 'All sources' : `${v.length} source${v.length === 1 ? '' : 's'}`)}
          />
        </div>
        <div className="slv-filter slv-filter-kind">
          <MultiSelectDropdown
            value={filters.kinds || []}
            onChange={(v) => setFilter('kinds', v)}
            options={kindOptions}
            placeholder="All types"
            searchable
            selectedInMenu
            searchPlaceholder="Search types…"
            hideTags
            summarize={(v) => (v.length === 0 ? 'All types' : `${v.length} type${v.length === 1 ? '' : 's'}`)}
          />
        </div>
        <div className="slv-filter slv-filter-sev">
          <Dropdown
            value={filters.severity}
            onChange={(v) => setFilter('severity', v)}
            options={SEVERITY_OPTIONS}
          />
        </div>
        <button
          type="button"
          className={clearFiltersClass(activeFilterCount > 0, 'btn-icon slv-clear')}
          onClick={clearFilters}
          disabled={activeFilterCount === 0}
          title={activeFilterCount === 0 ? 'No filters applied' : `Clear ${activeFilterCount} filter(s)`}
        >
          <span className="material-symbols-rounded">filter_alt_off</span>
        </button>
      </div>

      <div className="slv-body">
        {loading && entries.length === 0 ? (
          <div className="slv-status"><LoadingSpinner /></div>
        ) : error ? (
          <div className="slv-status slv-error">
            <span className="material-symbols-rounded">error</span> {error}
          </div>
        ) : grouped.length === 0 ? (
          <div className="slv-status">
            <span className="material-symbols-rounded">search_off</span>
            No log entries match these filters
          </div>
        ) : (
          grouped.map((g) => {
            const open = expandedDates.has(g.date);
            const errCount = g.rows.filter(r => r.severity === 'err').length;
            const warnCount = g.rows.filter(r => r.severity === 'warn').length;
            const sev = errCount ? 'err' : warnCount ? 'warn' : 'ok';
            return (
              <div key={g.date} className={`slv-date-group sev-${sev} ${open ? 'is-open' : ''}`}>
                <button
                  type="button"
                  className="slv-date-header"
                  onClick={() => toggleDate(g.date)}
                  aria-expanded={open}
                >
                  <span className="material-symbols-rounded slv-chevron" aria-hidden="true">
                    {open ? 'expand_more' : 'chevron_right'}
                  </span>
                  <span className="slv-date-label">{g.date}</span>
                  <span className="slv-spacer" />
                  {errCount > 0 && <span className="slv-badge slv-badge-err">{errCount}</span>}
                  {warnCount > 0 && <span className="slv-badge slv-badge-warn">{warnCount}</span>}
                  <span className="slv-date-count">{g.rows.length}</span>
                </button>
                {open && (
                  <div className="slv-date-body">
                    {g.rows.map((e) => (
                      <div key={e.uid} className={`slv-line sev-${e.severity}`}>
                        <button
                          type="button"
                          className="slv-line-toggle"
                          onClick={() => setExpandedUid(expandedUid === e.uid ? null : e.uid)}
                          aria-expanded={expandedUid === e.uid}
                        >
                          <span className="slv-ts">{formatTime(e.ts)}</span>
                          <span
                            className="slv-tag"
                            style={{ color: SOURCE_ACCENT[e.source] || '#b1bac4' }}
                          >
                            [{e.kind}]
                          </span>
                          <span className="slv-msg">{summarise(e)}</span>
                          <span className="slv-actor">{e.actor}</span>
                          <span className="material-symbols-rounded slv-caret" aria-hidden="true">
                            {expandedUid === e.uid ? 'expand_more' : 'chevron_right'}
                          </span>
                        </button>
                        {expandedUid === e.uid && noteOf(e.details) && (
                          <LogEntryNote
                            note={noteOf(e.details).note}
                            prompt={noteOf(e.details).prompt}
                          />
                        )}
                        {expandedUid === e.uid && (
                          <pre className="slv-details">
                            {JSON.stringify(
                              {
                                source: e.source,
                                kind: e.kind,
                                severity: e.severity,
                                actor: e.actor,
                                entity: e.entityIdentifier,
                                at: e.ts,
                                details: withoutNote(e.details)
                              },
                              null, 2
                            )}
                          </pre>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {total > PAGE_SIZE && (
        <div className="slv-pagination">
          <PaginationControls
            pagination={{ page, limit: PAGE_SIZE, total, totalPages }}
            onPageChange={setPage}
          />
        </div>
      )}
    </div>
  );
}

export default SystemLogViewer;
